import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Chip,
  Divider,
  Alert,
  Tooltip,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Group as GroupIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import BaseModal from '../Modal/BaseModal';
import { CustomButton } from '../Infra/CustomButton/CustomButton';
import StyledDataGrid from '../DataGrid/DataGridStyles';
import InactiveInstrumentsToggle from '../DataGrid/InactiveInstrumentsToggle';
import ConfirmationModal from '../Modal/ConfirmationModal';
import SortableGroupItem from './SortableGroupItem/SortableGroupItem';
import { ManageGroupsModalProps } from './types';
import { useManageGroups } from './useManageGroups';
import { useDragAndDrop } from './useDragAndDrop';
import { showSnackbar } from '../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../context/Snackbar/Types';
import { Group } from './types';
import { useGridSelectionHandler } from './useGridSelection';
import { getRowIdAsString } from './groupUtils';
import { getNumericStringSortComparator } from '../../utils/sortComparators';
import { isInactiveStatus } from '../../utils/instrumentStatusUtils';

// Helper function to check if a group has been modified
const checkIfGroupIsModified = (
  group: Group,
  activeGroupId: string | null,
  selectedRowIds: string[],
  newGroupName: string
): boolean => {
  if (activeGroupId !== group.id) {
    return false;
  }

  const rowIdsChanged =
    JSON.stringify([...group.rowIds].sort()) !==
    JSON.stringify([...selectedRowIds].sort());
  const nameChanged = group.name !== newGroupName;

  return rowIdsChanged || nameChanged;
};

const ManageGroupsModal: React.FC<ManageGroupsModalProps> = ({
  open,
  onClose,
  onSave,
  availableRows,
  existingGroups = [],
  title = 'Manage Groups',
  columns,
  hiddenColumns = [],
}) => {
  // State for showing inactive instruments
  const [showInactive, setShowInactive] = useState(false);

  // Filter out inactive instruments unless showInactive is true
  const activeAvailableRows = useMemo(() => {
    if (showInactive) {
      return availableRows;
    }
    return availableRows.filter(row => {
      const status = row.instrument_status as string;
      if (!status) return true; // Show rows without status
      return !isInactiveStatus(status);
    });
  }, [availableRows, showInactive]);

  // Convert columns to DataGrid format
  const dataGridColumns: GridColDef[] = [
    ...columns.map(
      (column): GridColDef => ({
        field: column.field,
        headerName: column.headerName,
        flex: 1,
        minWidth:
          column.field.includes('code') || column.field.includes('currency')
            ? 100
            : column.field.includes('name')
              ? 200
              : 180,
        getSortComparator: getNumericStringSortComparator,
        renderCell: params => {
          const value = column.getValue
            ? column.getValue(params.row)
            : params.row[column.field] || '';

          // Use column's custom renderCell if provided (e.g., for status chips)
          if (column.renderCell) {
            return column.renderCell({ value, row: params.row });
          }

          const displayValue = String(value);
          const shouldShowTooltip = displayValue.length > 25;

          return (
            <Tooltip
              title={displayValue}
              enterDelay={300}
              disableHoverListener={!shouldShowTooltip}
              placement="top"
            >
              <Box
                sx={{
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'default',
                }}
              >
                {displayValue}
              </Box>
            </Tooltip>
          );
        },
      })
    ),
  ];

  // Simple column visibility model based on hiddenColumns prop
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    const model: Record<string, boolean> = {};
    columns.forEach(col => {
      model[col.field] = !hiddenColumns.includes(col.field);
    });
    return model;
  });

  // Update column visibility model when columns or hiddenColumns change
  useEffect(() => {
    const model: Record<string, boolean> = {};
    columns.forEach(col => {
      model[col.field] = !hiddenColumns.includes(col.field);
    });
    setColumnVisibilityModel(model);
  }, [columns, hiddenColumns]);

  const {
    groups,
    activeGroupId,
    newGroupName,
    searchTerm,
    filteredRows,
    selectedRowIds,
    rowSelectionModel,
    setGroups,
    setActiveGroupId,
    setNewGroupName,
    setSearchTerm,
    setSelectedRowIds,
    clearSelection,
    handleGroupClick,
    createGroup,
    updateGroupNameAndRows,
    deleteGroup,
    calculateReorderedGroups,
    resetForm,
  } = useManageGroups({ availableRows: activeAvailableRows, existingGroups });

  // Setup drag-and-drop functionality
  const { sensors, handleDragEnd } = useDragAndDrop({
    groups,
    setGroups,
    calculateReorderedGroups,
    onSave,
  });

  const [error, setError] = useState<string>('');
  const selectedRowIdsRef = useRef<string[]>([]);

  // Sync selectedRowIds to ref when it changes (other code uses the ref)
  useEffect(() => {
    selectedRowIdsRef.current = selectedRowIds;
  }, [selectedRowIds]);

  // Delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    groupId: string;
    groupName: string;
  } | null>(null);

  // Handler for group item click
  const handleGroupItemClick = (group: Group) => {
    handleGroupClick(group.id, selectedRowIdsRef);
    // Populate the group name in the input field when selecting a group
    if (activeGroupId !== group.id) {
      setNewGroupName(group.name);
    } else {
      setNewGroupName('');
    }
  };

  // Handler for group deletion
  const handleGroupDelete = (group: Group) => {
    setDeleteConfirmation({
      open: true,
      groupId: group.id,
      groupName: group.name,
    });
  };

  useEffect(() => {
    if (!open) {
      resetForm();
      setError('');
      selectedRowIdsRef.current = []; // Clear ref when modal closes
    }
  }, [open, resetForm]);

  const handleCreateGroup = () => {
    try {
      if (!newGroupName.trim()) {
        setError('Group name cannot be empty');
        return;
      }

      const newGroup = createGroup(newGroupName, selectedRowIdsRef.current);

      // Save immediately to parent
      const updatedGroups = [...groups, newGroup];
      onSave(updatedGroups);

      // Show success notification
      showSnackbar({
        title: 'Created',
        message: `Group "${newGroup.name}" created successfully`,
        type: SnackbarType.success,
      });

      setError('');

      // Clear form after successful creation
      setNewGroupName('');
      selectedRowIdsRef.current = [];
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create group');
    }
  };

  const handleUpdateGroup = () => {
    try {
      if (!activeGroupId) {
        setError('No group selected for update');
        return;
      }

      if (!newGroupName.trim()) {
        setError('Group name cannot be empty');
        return;
      }

      // Use the combined function to update both name and rows
      updateGroupNameAndRows(
        activeGroupId,
        newGroupName,
        selectedRowIdsRef.current
      );

      // Manually construct updated groups for immediate save
      const updatedGroups = groups.map(group =>
        group.id === activeGroupId
          ? {
              ...group,
              name: newGroupName.trim(),
              rowIds: [...selectedRowIdsRef.current],
            }
          : group
      );

      // Save immediately to parent with updated groups
      onSave(updatedGroups);

      // Show success notification
      showSnackbar({
        title: 'Updated',
        message: `Group "${newGroupName}" updated successfully`,
        type: SnackbarType.info,
      });

      setError('');

      // Reset to default state after successful update
      setActiveGroupId(null);
      setSelectedRowIds([]);
      setNewGroupName('');
      selectedRowIdsRef.current = [];
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update group');
    }
  };

  const handleClose = () => {
    resetForm();
    setError('');
    onClose();
  };

  // Delete confirmation handlers
  const handleDeleteConfirm = () => {
    if (deleteConfirmation) {
      const groupName = deleteConfirmation.groupName;

      // Apply the deletion to local state
      deleteGroup(deleteConfirmation.groupId);

      // Save immediately to parent
      const updatedGroups = groups.filter(
        g => g.id !== deleteConfirmation.groupId
      );
      onSave(updatedGroups);

      // Show success notification
      showSnackbar({
        title: 'Deleted',
        message: `Group "${groupName}" deleted successfully`,
        type: SnackbarType.warning,
      });

      // Clear selectedRowIdsRef if the deleted group was active
      if (activeGroupId === deleteConfirmation.groupId) {
        selectedRowIdsRef.current = [];
      }

      setDeleteConfirmation(null);
      setError('');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation(null);
  };

  // Row selection handler using shared utility
  const handleRowSelectionModelChange = useGridSelectionHandler(
    selectedRowIds,
    setSelectedRowIds,
    filteredRows,
    {
      getRowId: getRowIdAsString,
    }
  );

  return (
    <div data-testid="dialog-groups">
      <BaseModal
        dataTestId="manage-groups-modal"
        open={open}
        onClose={handleClose}
        title={title}
        subtitle="Organize items into named categories"
        icon={<GroupIcon />}
        maxWidth="xl"
        width="90vw"
        maxWidthOverride="1400px"
        customFooter={
          <CustomButton
            onClick={handleClose}
            variant="text"
            sx={{ color: 'text.secondary' }}
          >
            Close
          </CustomButton>
        }
      >
        <Box
          sx={{ display: 'flex', gap: 3, minHeight: '70vh', maxHeight: '80vh' }}
        >
          {/* Left Panel - Groups */}
          <Box
            sx={{ width: '450px', display: 'flex', flexDirection: 'column' }}
          >
            {error && (
              <Alert
                severity="error"
                onClose={() => setError('')}
                sx={{ mb: 2 }}
              >
                {error}
              </Alert>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                EXISTING GROUPS
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
              <TextField
                size="small"
                placeholder="Enter group name..."
                value={newGroupName}
                onChange={e => setNewGroupName(e.target.value)}
                onKeyPress={e => {
                  if (e.key === 'Enter' && newGroupName.trim()) {
                    handleCreateGroup();
                  }
                }}
                sx={{ flex: 1, minWidth: '280px' }}
                InputProps={{
                  endAdornment: newGroupName ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setNewGroupName('')}
                        edge="end"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
              <CustomButton
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  if (activeGroupId) {
                    handleUpdateGroup();
                  } else {
                    handleCreateGroup();
                  }
                }}
                disabled={
                  !newGroupName.trim() ||
                  selectedRowIds.length === 0 ||
                  (activeGroupId &&
                    groups.find(g => g.id === activeGroupId)?.name ===
                      newGroupName &&
                    JSON.stringify(
                      [
                        ...(groups.find(g => g.id === activeGroupId)?.rowIds ||
                          []),
                      ].sort()
                    ) === JSON.stringify([...selectedRowIds].sort()))
                }
                sx={{
                  bgcolor: '#6366f1',
                  '&:hover': { bgcolor: '#5855eb' },
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  px: 2,
                  py: 0.5,
                }}
              >
                {activeGroupId ? 'UPDATE' : 'CREATE'}
              </CustomButton>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={groups.map(g => g.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {groups.map(group => (
                    <SortableGroupItem
                      key={group.id}
                      group={group}
                      isActive={activeGroupId === group.id}
                      isModified={checkIfGroupIsModified(
                        group,
                        activeGroupId,
                        selectedRowIds,
                        newGroupName
                      )}
                      onClick={() => handleGroupItemClick(group)}
                      onDelete={() => handleGroupDelete(group)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* Right Panel - Items Table */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Search Controls */}
            <Box sx={{ display: 'flex', mb: 2, alignItems: 'center', gap: 2 }}>
              <Box sx={{ position: 'relative', maxWidth: '400px' }}>
                <SearchIcon
                  sx={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'text.secondary',
                  }}
                />
                <TextField
                  size="small"
                  placeholder="Search instruments..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { pl: 5 }, width: '400px' }}
                  InputProps={{
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchTerm('')}
                          edge="end"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Box>
              <InactiveInstrumentsToggle
                checked={showInactive}
                onChange={setShowInactive}
              />
              {selectedRowIds.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography
                    variant="body2"
                    sx={{ color: 'primary.main', fontWeight: 500 }}
                  >
                    {selectedRowIds.length} selected
                  </Typography>
                  <CustomButton
                    size="small"
                    variant="text"
                    onClick={clearSelection}
                    sx={{
                      minWidth: 'auto',
                      p: 0.5,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      color: 'text.secondary',
                    }}
                  >
                    Clear
                  </CustomButton>
                </Box>
              )}
            </Box>

            {/* Items DataGrid */}
            <Box
              sx={{
                flex: 1,
                minHeight: '400px',
                maxHeight: '65vh',
                '& .MuiDataGrid-root': {
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  backgroundColor: 'white',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                },
                '& .MuiDataGrid-row': {
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(99, 102, 241, 0.08) !important',
                  },
                  '&:hover': {
                    backgroundColor: '#f9fafb !important',
                  },
                },
              }}
            >
              <StyledDataGrid
                rows={filteredRows || []}
                columns={dataGridColumns}
                getRowId={getRowIdAsString}
                checkboxSelection
                disableRowSelectionOnClick
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={handleRowSelectionModelChange}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={setColumnVisibilityModel}
                sx={{
                  height: '100%',
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f9fafb',
                    borderBottom: '1px solid #e5e7eb',
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    },
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #f3f4f6',
                    fontSize: '0.875rem',
                    color: 'text.primary',
                  },
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          open={deleteConfirmation?.open || false}
          title="Delete Group"
          message={
            <>
              Are you sure you want to delete the group{' '}
              <strong>"{deleteConfirmation?.groupName}"</strong>?
              <br />
              <br />
              This action cannot be undone and will remove the group from your
              configuration.
            </>
          }
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          severity="error"
        />
      </BaseModal>
    </div>
  );
};

export default ManageGroupsModal;
